import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useLocation,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect } from "react";

import appCss from "../styles.css?url";
import { LanguageProvider, useLanguage } from "@/i18n/LanguageProvider";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { DefaultCatchBoundary } from "@/components/ui/catch-boundary";
import { DefaultPendingComponent } from "@/components/ui/pending-component";
import logoSrc from "@/assets/logo.png";

function NotFoundComponent() {
  const { t } = useLanguage();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <div className="text-8xl mb-4 animate-wiggle inline-block">🤖</div>
        <h1 className="text-7xl font-extrabold bg-gradient-primary bg-clip-text text-transparent">
          404
        </h1>
        <h2 className="mt-4 text-xl font-bold">{t.notFound.title}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t.notFound.desc}</p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full bg-gradient-primary px-6 py-3 text-sm font-bold text-primary-foreground shadow-playful"
          >
            {t.notFound.cta}
          </Link>
        </div>
      </div>
    </div>
  );
}

// ErrorComponent replaced by DefaultCatchBoundary

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Code Cubs Academy — Fun coding classes for kids 6-16" },
      {
        name: "description",
        content:
          "Live, project-based coding classes for kids: Scratch, Python, Web Dev and AI. Small groups, expert mentors, parent dashboard.",
      },
      { property: "og:title", content: "Code Cubs Academy — Fun coding classes for kids 6-16" },
      {
        property: "og:description",
        content:
          "Live, project-based coding classes for kids: Scratch, Python, Web Dev and AI. Small groups, expert mentors, parent dashboard.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Code Cubs Academy — Fun coding classes for kids 6-16" },
      {
        name: "twitter:description",
        content:
          "Live, project-based coding classes for kids: Scratch, Python, Web Dev and AI. Small groups, expert mentors, parent dashboard.",
      },
      {
        property: "og:image",
        content:
          "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=1200",
      },
      {
        name: "twitter:image",
        content:
          "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=1200",
      },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "icon", href: logoSrc, type: "image/png" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: DefaultCatchBoundary,
  pendingComponent: DefaultPendingComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function AuthInvalidator() {
  const router = useRouter();
  const queryClient = useQueryClient();
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(() => {
      router.invalidate();
      queryClient.invalidateQueries();
    });
    return () => subscription.unsubscribe();
  }, [router, queryClient]);
  return null;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const location = useLocation();
  const isAuthRoute = [
    "/login",
    "/signup",
    "/forgot-password",
    "/verify-email",
    "/update-password",
  ].includes(location.pathname);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LanguageProvider>
          <AuthInvalidator />
          <div className="min-h-screen flex flex-col">
            {!isAuthRoute && <Header />}
            <main className="flex-1">
              <Outlet />
            </main>
            {!isAuthRoute && <Footer />}
          </div>
          <Toaster />
        </LanguageProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
